// Minimální ZIP čtení/zápis pro .bcfzip — plně offline, bez externí knihovny.
// Dekomprese/komprese přes nativní DecompressionStream/CompressionStream
// ("deflate-raw"). Zápis používá STORE i DEFLATE. Ochrana proti path traversal (§38).
//
// Hardening (§15, etapa 2 celek B): kromě path-traversal se při čtení vynucují
// limity proti zip-bomb — počet položek, rozbalená velikost (per-entry i celková)
// a kompresní poměr. Deklarovaná velikost z central directory se kontroluje JEŠTĚ
// PŘED inflací, aby se bomba vůbec nerozbalila; po inflaci se ověří i skutečná délka.

const te = new TextEncoder();
const td = new TextDecoder();

// Výchozí limity (lze přepsat parametrem). Legitimní .bcfzip je řádově malý.
export const ZIP_LIMITS = {
  maxEntries: 10000,               // počet položek v archivu
  maxEntryBytes: 128 * 1024 * 1024, // rozbalená velikost jedné položky
  maxTotalBytes: 512 * 1024 * 1024, // rozbalená velikost celého archivu
  maxRatio: 200,                    // rozbalená/komprimovaná na položku
};

// Chyba překročení limitu — volající ji mapuje na doménový kód (BCF_INVALID / zip-bomb).
export class ZipLimitError extends Error {
  constructor(message) { super(message); this.name = "ZipLimitError"; this.code = "ZIP_LIMIT_EXCEEDED"; }
}

// --- CRC32 ------------------------------------------------------------------
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; }
  return t;
})();
function crc32(bytes) {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

async function inflateRaw(bytes) {
  if (typeof DecompressionStream === "undefined") throw new Error("DecompressionStream není k dispozici (deflate).");
  const ds = new DecompressionStream("deflate-raw");
  const stream = new Blob([bytes]).stream().pipeThrough(ds);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}
async function deflateRaw(bytes) {
  if (typeof CompressionStream === "undefined") return null;
  const cs = new CompressionStream("deflate-raw");
  const stream = new Blob([bytes]).stream().pipeThrough(cs);
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

// Bezpečné jméno cesty v ZIP — zákaz absolutních cest a "..".
function safeName(name) {
  const n = String(name).replace(/\\/g, "/");
  if (n.startsWith("/") || n.split("/").some((p) => p === "..")) return null;
  return n;
}

// --- Čtení ------------------------------------------------------------------
// Vrací Map<path, Uint8Array>. `limits` přepisuje ZIP_LIMITS (hardening §15).
export async function zipRead(input, limits) {
  const lim = { ...ZIP_LIMITS, ...(limits || {}) };
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  // Najít EOCD (0x06054b50) od konce.
  let eocd = -1;
  for (let i = bytes.length - 22; i >= 0; i--) { if (dv.getUint32(i, true) === 0x06054b50) { eocd = i; break; } }
  if (eocd < 0) throw new Error("Neplatný ZIP: EOCD nenalezen.");
  const cdCount = dv.getUint16(eocd + 10, true);
  let cdOffset = dv.getUint32(eocd + 16, true);
  if (cdCount > lim.maxEntries) throw new ZipLimitError(`ZIP: příliš mnoho položek (${cdCount} > ${lim.maxEntries}).`);

  const out = new Map();
  let p = cdOffset;
  let total = 0;
  for (let i = 0; i < cdCount; i++) {
    if (dv.getUint32(p, true) !== 0x02014b50) break;
    const method = dv.getUint16(p + 10, true);
    const compSize = dv.getUint32(p + 20, true);
    const uncompSize = dv.getUint32(p + 24, true);   // deklarovaná rozbalená velikost (CD)
    const nameLen = dv.getUint16(p + 28, true);
    const extraLen = dv.getUint16(p + 30, true);
    const commentLen = dv.getUint16(p + 32, true);
    const localOff = dv.getUint32(p + 42, true);
    const name = td.decode(bytes.subarray(p + 46, p + 46 + nameLen));
    const safe = safeName(name);
    if (safe != null && !name.endsWith("/")) {
      // Kontrola PŘED inflací — zip-bomb se vůbec nerozbalí.
      if (uncompSize > lim.maxEntryBytes) throw new ZipLimitError(`ZIP: položka „${safe}" je příliš velká (${uncompSize} B).`);
      if (compSize > 0 && uncompSize / compSize > lim.maxRatio) throw new ZipLimitError(`ZIP: podezřelý kompresní poměr u „${safe}".`);
      if (total + uncompSize > lim.maxTotalBytes) throw new ZipLimitError("ZIP: celková rozbalená velikost překračuje limit.");
      // Data z lokální hlavičky.
      const lNameLen = dv.getUint16(localOff + 26, true);
      const lExtraLen = dv.getUint16(localOff + 28, true);
      const dataStart = localOff + 30 + lNameLen + lExtraLen;
      const comp = bytes.subarray(dataStart, dataStart + compSize);
      const data = method === 8 ? await inflateRaw(comp) : comp.slice();
      // Ověřit skutečnou délku (deklarace v CD nemusí odpovídat).
      if (data.length > lim.maxEntryBytes) throw new ZipLimitError(`ZIP: rozbalená položka „${safe}" překračuje limit.`);
      total += data.length;
      if (total > lim.maxTotalBytes) throw new ZipLimitError("ZIP: celková rozbalená velikost překračuje limit.");
      out.set(safe, data);
    }
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

// --- Zápis ------------------------------------------------------------------
// files: Map<path, Uint8Array|string>. Vrací Uint8Array (celý ZIP).
export async function zipWrite(files) {
  const entries = [];
  const locals = [];
  let offset = 0;

  for (const [name, content] of files) {
    const nameBytes = te.encode(name);
    const raw = typeof content === "string" ? te.encode(content) : (content instanceof Uint8Array ? content : new Uint8Array(content));
    const crc = crc32(raw);
    let method = 0, data = raw;
    const deflated = await deflateRaw(raw);
    if (deflated && deflated.length < raw.length) { method = 8; data = deflated; }

    const local = new Uint8Array(30 + nameBytes.length + data.length);
    const ldv = new DataView(local.buffer);
    ldv.setUint32(0, 0x04034b50, true);
    ldv.setUint16(4, 20, true);          // version
    ldv.setUint16(6, 0, true);           // flags
    ldv.setUint16(8, method, true);
    ldv.setUint16(10, 0, true);          // time
    ldv.setUint16(12, 0x21, true);       // date
    ldv.setUint32(14, crc, true);
    ldv.setUint32(18, data.length, true);
    ldv.setUint32(22, raw.length, true);
    ldv.setUint16(26, nameBytes.length, true);
    ldv.setUint16(28, 0, true);
    local.set(nameBytes, 30);
    local.set(data, 30 + nameBytes.length);
    locals.push(local);

    entries.push({ nameBytes, crc, method, compSize: data.length, size: raw.length, offset });
    offset += local.length;
  }

  // Central directory.
  const cdParts = [];
  let cdSize = 0;
  for (const e of entries) {
    const cd = new Uint8Array(46 + e.nameBytes.length);
    const cdv = new DataView(cd.buffer);
    cdv.setUint32(0, 0x02014b50, true);
    cdv.setUint16(4, 20, true);
    cdv.setUint16(6, 20, true);
    cdv.setUint16(8, 0, true);
    cdv.setUint16(10, e.method, true);
    cdv.setUint16(12, 0, true);
    cdv.setUint16(14, 0x21, true);
    cdv.setUint32(16, e.crc, true);
    cdv.setUint32(20, e.compSize, true);
    cdv.setUint32(24, e.size, true);
    cdv.setUint16(28, e.nameBytes.length, true);
    cdv.setUint32(42, e.offset, true);
    cd.set(e.nameBytes, 46);
    cdParts.push(cd);
    cdSize += cd.length;
  }

  const eocd = new Uint8Array(22);
  const edv = new DataView(eocd.buffer);
  edv.setUint32(0, 0x06054b50, true);
  edv.setUint16(8, entries.length, true);
  edv.setUint16(10, entries.length, true);
  edv.setUint32(12, cdSize, true);
  edv.setUint32(16, offset, true);

  const total = offset + cdSize + eocd.length;
  const result = new Uint8Array(total);
  let pos = 0;
  for (const l of locals) { result.set(l, pos); pos += l.length; }
  for (const c of cdParts) { result.set(c, pos); pos += c.length; }
  result.set(eocd, pos);
  return result;
}
